import { useState } from 'react';
import {
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Textarea,
    useToast,
} from '@chakra-ui/react';
import { FiCheck, FiX } from 'react-icons/fi';

import { approveRevisionRequest, declineRevisionRequest } from '../../api/revision-api';
import type { RevisionRequest } from '../../types/revision.type';
import { Modal } from 'newui/components/ui/Modal';
import { DateInputWithLabel, DateTimePickerWithLabel, TextAreaWithLabel, TimePickerWithLabel } from 'newui/components/ui/InputWithLabel';
import { ToggleButton } from 'newui/components/ToggleButton';
import { parseTime, time12To24 } from 'newui/components/ui/InputWithLabel/TimePickerWithLabel';

export interface RevisionRequestDialogProps {
    isOpen: boolean;
    responseFromCompanyName: string;
    revisionRequest: RevisionRequest;
    onActionComplete: () => void;
    onClose: () => void;
}

type DialogMode = 'view' | 'approve' | 'decline';

const DEFAULT_RESUBMIT_DAYS = 2;

const getDefaultResubmitBy = (): string => {
    const date = new Date();
    date.setHours(date.getHours() + (DEFAULT_RESUBMIT_DAYS * 24));
    return date.toISOString().slice(0, 16);
};

const RevisionRequestDialog: React.FC<RevisionRequestDialogProps> = ({
    isOpen,
    onClose,
    revisionRequest,
    responseFromCompanyName,
    onActionComplete,
}) => {
    const toast = useToast();
    const [mode, setMode] = useState<DialogMode>('view');
    const [isLoading, setIsLoading] = useState(false);
    const [resubmitBy, setResubmitBy] = useState(getDefaultResubmitBy());
    const [declinedReason, setDeclinedReason] = useState('');
    const [declinedReasonError, setDeclinedReasonError] = useState('');
    const [isRespondByChecked, setIsRespondByChecked] = useState(true);

    const handleClose = () => {
        setMode('view');
        setDeclinedReason('');
        setDeclinedReasonError('');
        setResubmitBy(getDefaultResubmitBy());
        onClose();
    };

    const onRespondByChanged = (date: Date, time: string, timezone: string) => {
        // Extract hours and minutes from the time string, e.g., "14:30"
        const timeValue = time12To24(parseTime(time));

        // Create a new Date based on the incoming date
        const combinedDate = new Date(date);
        combinedDate.setHours(timeValue.hourNum, timeValue.minuteNum, 0, 0); // set hours, minutes, seconds, ms

        // Convert to ISO string in the specified timezone if needed
        // Using Intl.DateTimeFormat to shift the time to target timezone
        // Note: JavaScript Date is always UTC internally
        const isoString = new Date(
            combinedDate.toLocaleString('en-US', { timeZone: timezone })
        ).toISOString();

        setResubmitBy(isoString);
    };

    const handleApprove = async () => {
        setIsLoading(true);
        try {
            await approveRevisionRequest(
                revisionRequest.rfx_invite_id,
                revisionRequest.id,
                { resubmit_by: isRespondByChecked ? new Date(resubmitBy).toISOString() : undefined }
            );
            toast({
                title: 'Revision approved',
                description: 'The quote revision request has been approved.',
                status: 'success',
                duration: 4000,
                isClosable: true,
            });
            onActionComplete();
            handleClose();
        } catch {
            toast({
                title: 'Error',
                description: 'Failed to approve revision request.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleDecline = async () => {
        if (!declinedReason.trim()) {
            setDeclinedReasonError('Reason is required');
            return;
        }
        setIsLoading(true);
        try {
            await declineRevisionRequest(
                revisionRequest.rfx_invite_id,
                revisionRequest.id,
                { declined_reason: declinedReason }
            );
            toast({
                title: 'Revision declined',
                description: 'The quote revision request has been declined.',
                status: 'info',
                duration: 4000,
                isClosable: true,
            });
            onActionComplete();
            handleClose();
        } catch {
            toast({
                title: 'Error',
                description: 'Failed to decline revision request.',
                status: 'error',
                duration: 4000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const renderViewMode = () => (
        <>
            <ModalBody>
                <div className="grid grid-cols-1 gap-4">
                    <div className='flex flex-row'>
                        {revisionRequest.requested_by?.fullName} from {responseFromCompanyName} has requested a revision to their {revisionRequest.type?.replace('_', ' ')}.
                    </div>

                    {/* Reason */}
                    <div>
                        <span className="text-sm font-medium text-gray-800 md:w-1/3 mb-1 md:mb-0">
                            Reason
                        </span>
                        <p className="rounded-lg bg-gray-50 p-3 text-gray-900">
                            {revisionRequest.reason}
                        </p>
                    </div>

                    <div>
                        You can choose to approve or decline this revision request.
                    </div>
                    <div>
                        <strong className='text-gray-500'>Approve:</strong> You can set a deadline for them to submit a revised quote.
                    </div>
                    <div>
                        <strong className='text-gray-500'>Decline:</strong> You will be able to provide a reason for declining the request.
                    </div>
                </div>
            </ModalBody>
            <ModalFooter gap={3}>
                <Button
                    variant="outline"
                    onClick={handleClose}
                    data-testid="dialog-cancel-btn"
                >
                    Cancel
                </Button>
                <Button
                    colorScheme="red"
                    variant="outline"
                    leftIcon={<FiX />}
                    onClick={() => setMode('decline')}
                    data-testid="dialog-decline-btn"
                >
                    Decline
                </Button>
                <Button
                    colorScheme="green"
                    leftIcon={<FiCheck />}
                    onClick={() => setMode('approve')}
                    data-testid="dialog-approve-btn"
                >
                    Approve
                </Button>
            </ModalFooter>
        </>
    );

    const renderApproveMode = () => (
        <>
            <ModalBody minH={"270px"}>
                <p className="mb-4 text-gray-600">
                    Set a deadline for the supplier to resubmit their revised quote.
                </p>
                <div className="mb-4">
                    <ToggleButton checked={isRespondByChecked} onClick={() => setIsRespondByChecked(!isRespondByChecked)}>Add Respond By Date</ToggleButton>
                </div>
                {isRespondByChecked && <DateTimePickerWithLabel label='Resubmit By' required dateFieldName='responseDate' timeFieldName='responseTime' timeZoneFieldName='responseTimeZone' defaultDaysFromToday={DEFAULT_RESUBMIT_DAYS} onValueChanged={onRespondByChanged} />}
            </ModalBody>
            <ModalFooter gap={3}>
                <Button
                    variant="ghost"
                    onClick={() => setMode('view')}
                    isDisabled={isLoading}
                    data-testid="dialog-back-btn"
                >
                    Back
                </Button>
                <Button
                    colorScheme="green"
                    onClick={handleApprove}
                    isLoading={isLoading}
                    leftIcon={<FiCheck />}
                    data-testid="dialog-confirm-approve-btn"
                >
                    Approve
                </Button>
            </ModalFooter>
        </>
    );

    const renderDeclineMode = () => (
        <>
            <ModalBody>
                <div className='mb-4 flex-col gap-4 flex'>
                    <div>
                        Please provide a reason for declining this revision request.
                    </div>
                    <div>This will be shared with the supplier to help them understand why their request was declined and what they can do to address the issue.</div>
                </div>
                <TextAreaWithLabel
                    label='Reason for Declining'
                    placeholder="Please explain why you are declining this revision request..."
                    rows={4}
                    value={declinedReason}
                    hasError={!!declinedReasonError}
                    errorText={declinedReasonError}
                    onChange={(e) => {
                        setDeclinedReason(e.target.value);
                        if (e.target.value.trim()) {
                            setDeclinedReasonError('');
                        }
                    }}
                    data-testid="decline-reason-textarea"
                    required
                />
            </ModalBody>
            <ModalFooter gap={3}>
                <Button
                    variant="ghost"
                    onClick={() => setMode('view')}
                    isDisabled={isLoading}
                    data-testid="dialog-back-btn"
                >
                    Back
                </Button>
                <Button
                    colorScheme="red"
                    onClick={handleDecline}
                    isLoading={isLoading}
                    leftIcon={<FiX />}
                    data-testid="dialog-confirm-decline-btn"
                >
                    Decline
                </Button>
            </ModalFooter>
        </>
    );

    const headerTitle = mode === 'view' ? 'Quote Revision Request'
        : mode === 'approve' ? 'Approve Revision Request'
            : mode === 'decline' && 'Decline Revision Request';

    return (
        <Modal
            title={headerTitle}
            description={`Requested on ${new Date(revisionRequest.created_at).toLocaleString()}`}
            isOpen={isOpen}
            onClose={handleClose}
            size="lg"
            modalType='standard'
            iconType='info'
        >
            {mode === 'view' && renderViewMode()}
            {mode === 'approve' && renderApproveMode()}
            {mode === 'decline' && renderDeclineMode()}
        </Modal>
    );
};

export { RevisionRequestDialog };
